/**
 * Journey timing: how long the core journeys take for real members, split
 * into time waiting on the server and time spent in the client.
 *
 * A journey runs from a user action (or navigation start, for app_launch) to
 * its result being painted, and sends ONE PostHog event:
 *
 *   journey_timing { journey, total_ms, server_ms, client_ms, platform,
 *                    app_version, route, cold, ...meta }
 *
 * - server_ms is wall time with at least one Supabase request in flight
 *   (src/utils/journeyServerClock.ts wraps the client's fetch), plus anything
 *   a caller wraps with handle.trackServer(). client_ms = total_ms - server_ms.
 * - cold is true for app_launch and for the first run of a journey name in
 *   this page load (lazy chunks and query caches are still empty).
 * - route is the destination path with ids replaced by ":id".
 * - A journey is dropped, never sent, when it runs past 60 seconds, when the
 *   tab or app is hidden while it runs, when the member navigates away from
 *   its routes, or when it ends a second time.
 *
 * Starting points: route-driven journeys (program_open, programs_tab,
 * journey_session_open, session_start) start in noteJourneyRoute(), called by
 * RouteTracking in App.tsx, using the time of the tap that caused the
 * navigation. app_launch starts in installJourneyTiming() (entry-client).
 * paywall_open starts in useOpenJourney() when a paywall opens.
 *
 * The event goes through trackEvent() to PostHog only, so the usual consent,
 * internal-user and local-dev gates apply. Meta values are short enums,
 * numbers and booleans chosen by the call site, never user content. Every
 * function here swallows its own errors: timing must never affect the UI.
 */
import * as React from 'react';
import { Capacitor } from '@capacitor/core';
import { trackEvent } from '@/utils/trackingManager';
import { peekAppVersionInfo, resolveAppVersionInfo, UNKNOWN_APP_VERSION } from '@/utils/appVersion';
import { subscribeServerActivity } from '@/utils/journeyServerClock';

export const JOURNEY_NAMES = [
  'app_launch',
  'session_start',
  'program_open',
  'journey_session_open',
  'paywall_open',
  'programs_tab',
] as const;

export type JourneyName = (typeof JOURNEY_NAMES)[number];

export const JOURNEY_TIMING_EVENT = 'journey_timing';
/** Journeys longer than this are abandoned (someone walked away). */
export const JOURNEY_MAX_MS = 60_000;
/** A navigation this soon after a tap counts as caused by that tap. */
export const JOURNEY_INTERACTION_WINDOW_MS = 1_000;

export type JourneyMetaValue = string | number | boolean | null;
export type JourneyMeta = Record<string, JourneyMetaValue | undefined>;

export type JourneyAbandonReason = 'timeout' | 'hidden' | 'route' | 'replaced' | 'closed' | 'manual';

export interface StartJourneyOptions {
  /** performance.now() timebase. Defaults to now; 0 means navigation start. */
  startTime?: number;
  /** Defaults to "first run of this name in this page load". */
  cold?: boolean;
  /** Paths the journey may visit; any other path abandons it. */
  routes?: readonly RegExp[];
}

export interface JourneyHandle {
  readonly name: JourneyName;
  readonly isActive: boolean;
  /** Manual server window, for work that does not go through Supabase fetch. */
  mark(point: 'server_start' | 'server_end'): void;
  /** Counts the time `work` is pending as server time. Returns `work`. */
  trackServer<T>(work: Promise<T>): Promise<T>;
  /** Sends the event. False when the journey was already ended or dropped. */
  end(extra?: JourneyMeta): boolean;
  abandon(reason?: JourneyAbandonReason): void;
}

// The keys every event carries; meta can never overwrite them.
const CORE_KEYS = new Set([
  'journey',
  'total_ms',
  'server_ms',
  'client_ms',
  'platform',
  'app_version',
  'route',
  'cold',
]);

const SEND_SKIP_PLATFORMS = ['ga4', 'meta', 'google_ads', 'tiktok', 'appsflyer'] as const;
const STATIC_ROUTE_SEGMENTS = new Set(['learn', 'start', 'session', 'prompts', 'edit', 'new']);

function now(): number {
  try {
    return performance.now();
  } catch {
    return Date.now();
  }
}

function isDocumentHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

/** Path with ids and slugs replaced by ":id"; no query string or hash. */
export function normalizeJourneyRoute(pathname: string | null | undefined): string {
  if (!pathname) return '/';
  const path = pathname.split(/[?#]/)[0] ?? '';
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) return '/';
  return `/${segments
    .map((segment, index) => (index === 0 || STATIC_ROUTE_SEGMENTS.has(segment) ? segment : ':id'))
    .join('/')}`;
}

function currentPathname(): string {
  return typeof window === 'undefined' ? '/' : window.location.pathname;
}

function readPlatform(): string {
  try {
    return Capacitor.getPlatform();
  } catch {
    return 'web';
  }
}

function cleanMeta(meta: JourneyMeta | undefined): Record<string, JourneyMetaValue> {
  const result: Record<string, JourneyMetaValue> = {};
  if (!meta) return result;
  for (const [key, value] of Object.entries(meta)) {
    if (value === undefined || CORE_KEYS.has(key)) continue;
    if (typeof value === 'string') {
      // Meta is for short enums; cap anything longer so free text cannot leak.
      result[key] = value.slice(0, 64);
      continue;
    }
    if (typeof value === 'number' && !Number.isFinite(value)) continue;
    result[key] = value;
  }
  return result;
}

// ── State ─────────────────────────────────────────────────────────────────

const active = new Map<JourneyName, JourneyRun>();
const startedNames = new Set<JourneyName>();
let lastInteractionAt: number | null = null;
let lastRoute: string | null = null;
let installed = false;
let visibilityListenerAttached = false;
let sendImpl: (properties: Record<string, JourneyMetaValue>) => void = defaultSend;

function defaultSend(properties: Record<string, JourneyMetaValue>): void {
  const send = (appVersion: string) => {
    void trackEvent(
      JOURNEY_TIMING_EVENT,
      { ...properties, app_version: appVersion },
      { clientOnly: true, skipPlatforms: SEND_SKIP_PLATFORMS },
    ).catch(() => undefined);
  };
  const known = peekAppVersionInfo();
  if (known) {
    send(known.appVersion);
    return;
  }
  void resolveAppVersionInfo(1_000)
    .then((info) => send(info?.appVersion ?? UNKNOWN_APP_VERSION))
    .catch(() => undefined);
}

function handleVisibilityChange(): void {
  if (!isDocumentHidden()) return;
  for (const run of Array.from(active.values())) run.abandon('hidden');
}

function ensureVisibilityListener(): void {
  if (visibilityListenerAttached || typeof document === 'undefined') return;
  try {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    visibilityListenerAttached = true;
  } catch {
    // ignore
  }
}

/** True when the page was hidden at any point after `since` (Chromium only). */
function wasHiddenSince(since: number): boolean {
  try {
    const entries = performance.getEntriesByType('visibility-state') as PerformanceEntry[];
    return entries.some((entry) => entry.name === 'hidden' && entry.startTime >= since);
  } catch {
    return false;
  }
}

class JourneyRun implements JourneyHandle {
  readonly name: JourneyName;
  readonly startedAt: number;
  private readonly startTime: number;
  private readonly meta: Record<string, JourneyMetaValue>;
  private readonly cold: boolean;
  private readonly routes: readonly RegExp[] | null;
  private readonly originPath: string;
  private done = false;
  private inflight = 0;
  private busySince = 0;
  private serverMs = 0;
  private manualOpen = false;
  private readonly unsubscribe: () => void;

  constructor(name: JourneyName, meta: JourneyMeta | undefined, options: StartJourneyOptions) {
    this.name = name;
    const current = now();
    const requested = options.startTime;
    this.startTime =
      typeof requested === 'number' && Number.isFinite(requested) && requested >= 0 && requested <= current
        ? requested
        : current;
    this.startedAt = this.startTime;
    this.meta = cleanMeta(meta);
    this.cold = options.cold ?? !startedNames.has(name);
    startedNames.add(name);
    this.routes = options.routes ?? null;
    this.originPath = currentPathname();
    this.unsubscribe = subscribeServerActivity({
      onRequestStart: () => this.serverStart(),
      onRequestEnd: () => this.serverEnd(),
    });
    if (isDocumentHidden() || wasHiddenSince(this.startTime)) this.abandon('hidden');
  }

  get isActive(): boolean {
    if (this.done) return false;
    if (now() - this.startTime > JOURNEY_MAX_MS) {
      this.abandon('timeout');
      return false;
    }
    return true;
  }

  private serverStart(): void {
    if (this.done) return;
    if (this.inflight === 0) this.busySince = now();
    this.inflight += 1;
  }

  private serverEnd(): void {
    if (this.done || this.inflight === 0) return;
    this.inflight -= 1;
    if (this.inflight === 0) this.serverMs += now() - this.busySince;
  }

  allowsPath(pathname: string): boolean {
    if (!this.routes) return true;
    if (pathname === this.originPath) return true;
    return this.routes.some((pattern) => pattern.test(pathname));
  }

  mark(point: 'server_start' | 'server_end'): void {
    try {
      if (point === 'server_start') {
        if (this.manualOpen) return;
        this.manualOpen = true;
        this.serverStart();
        return;
      }
      if (!this.manualOpen) return;
      this.manualOpen = false;
      this.serverEnd();
    } catch {
      // ignore
    }
  }

  trackServer<T>(work: Promise<T>): Promise<T> {
    try {
      this.serverStart();
      const settle = () => this.serverEnd();
      Promise.resolve(work).then(settle, settle);
    } catch {
      // ignore
    }
    return work;
  }

  end(extra?: JourneyMeta): boolean {
    try {
      if (!this.isActive) return false;
      const endTime = now();
      const total = endTime - this.startTime;
      if (isDocumentHidden() || wasHiddenSince(this.startTime)) {
        this.abandon('hidden');
        return false;
      }
      let server = this.serverMs;
      if (this.inflight > 0) server += endTime - this.busySince;
      this.finish();
      const totalMs = Math.max(0, Math.round(total));
      const serverMs = Math.min(totalMs, Math.max(0, Math.round(server)));
      sendImpl({
        ...this.meta,
        ...cleanMeta(extra),
        journey: this.name,
        total_ms: totalMs,
        server_ms: serverMs,
        client_ms: totalMs - serverMs,
        platform: readPlatform(),
        route: normalizeJourneyRoute(currentPathname()),
        cold: this.cold,
      });
      return true;
    } catch {
      this.finish();
      return false;
    }
  }

  abandon(_reason: JourneyAbandonReason = 'manual'): void {
    this.finish();
  }

  private finish(): void {
    if (this.done) return;
    this.done = true;
    try {
      this.unsubscribe();
    } catch {
      // ignore
    }
    if (active.get(this.name) === this) active.delete(this.name);
  }
}

const INERT_HANDLE = (name: JourneyName): JourneyHandle => ({
  name,
  isActive: false,
  mark: () => {},
  trackServer: <T,>(work: Promise<T>) => work,
  end: () => false,
  abandon: () => {},
});

// ── Public API ────────────────────────────────────────────────────────────

/**
 * Starts a journey and makes it the active one for its name (an older run of
 * the same name is dropped). Returns a handle; ending through the handle or
 * through endJourney(name) is equivalent.
 */
export function startJourney(
  name: JourneyName,
  meta?: JourneyMeta,
  options: StartJourneyOptions = {},
): JourneyHandle {
  try {
    ensureVisibilityListener();
    active.get(name)?.abandon('replaced');
    const run = new JourneyRun(name, meta, options);
    if (run.isActive) active.set(name, run);
    return run;
  } catch {
    return INERT_HANDLE(name);
  }
}

export function getActiveJourney(name: JourneyName): JourneyHandle | null {
  const run = active.get(name);
  return run && run.isActive ? run : null;
}

/** Ends the active journey of this name. False when none is running. */
export function endJourney(name: JourneyName, extra?: JourneyMeta): boolean {
  const run = getActiveJourney(name);
  return run ? run.end(extra) : false;
}

export function abandonJourney(name: JourneyName, reason: JourneyAbandonReason = 'manual'): void {
  active.get(name)?.abandon(reason);
}

/** Time of the latest tap or key press, if it happened within `windowMs`. */
export function getRecentInteractionTime(windowMs = JOURNEY_INTERACTION_WINDOW_MS): number | null {
  if (lastInteractionAt === null) return null;
  return now() - lastInteractionAt <= windowMs ? lastInteractionAt : null;
}

function recordInteraction(event: Event): void {
  const stamp = typeof event.timeStamp === 'number' && event.timeStamp > 0 ? event.timeStamp : now();
  // Some engines report epoch-based timestamps; fall back to now() for those.
  lastInteractionAt = stamp <= now() ? stamp : now();
}

const SESSION_PLAYER_PATH = /^\/(?:session\/[^/]+|daily-prompt\/session)\/?$/;
const PROGRAM_PATH = /^\/program\/[^/]+\/?$/;
const PROGRAMS_PATH = /^\/programs\/?$/;
const JOURNEY_PLAYER_PATH = /^\/journey\/[^/]+\/(?:start|learn)\/?$/;
const HOME_LAUNCH_PATH = /^\/(?:home)?\/?$/;

interface RouteJourneyRule {
  name: JourneyName;
  match: RegExp;
}

const ROUTE_JOURNEYS: readonly RouteJourneyRule[] = [
  { name: 'session_start', match: SESSION_PLAYER_PATH },
  { name: 'program_open', match: PROGRAM_PATH },
  { name: 'programs_tab', match: PROGRAMS_PATH },
  { name: 'journey_session_open', match: JOURNEY_PLAYER_PATH },
];

function readTabParam(search: string | undefined): string {
  try {
    const tab = new URLSearchParams(search ?? '').get('tab');
    return tab === 'journeys' || tab === 'podcast' ? tab : 'prompts';
  } catch {
    return 'prompts';
  }
}

/**
 * Route hook, called by RouteTracking on every pathname change. Drops
 * journeys that left their routes, then starts the route-driven journey for
 * the new path when a tap caused the navigation.
 */
export function noteJourneyRoute(pathname: string, search?: string): void {
  try {
    const previous = lastRoute;
    lastRoute = pathname;
    if (previous === pathname) return;
    for (const run of Array.from(active.values())) {
      if (!run.allowsPath(pathname)) run.abandon('route');
    }
    if (previous === null) return;
    const tapAt = getRecentInteractionTime();
    if (tapAt === null) return;
    const rule = ROUTE_JOURNEYS.find((candidate) => candidate.match.test(pathname));
    if (!rule) return;
    // A redirect inside the same journey (/journey/:id/start to /learn) keeps
    // the run that this same tap started.
    const running = active.get(rule.name);
    if (running && running.isActive && running.startedAt === tapAt) return;
    const meta: JourneyMeta = { from: normalizeJourneyRoute(previous) };
    if (rule.name === 'programs_tab') meta.tab = readTabParam(search);
    startJourney(rule.name, meta, { startTime: tapAt, routes: [rule.match] });
  } catch {
    // ignore
  }
}

/**
 * Installs the tap listener and starts app_launch when the page opens on
 * Home (or "/", which redirects members there). Call once at startup.
 */
export function installJourneyTiming(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  try {
    for (const type of ['pointerup', 'click', 'keydown'] as const) {
      window.addEventListener(type, recordInteraction, { capture: true, passive: true });
    }
    if (HOME_LAUNCH_PATH.test(window.location.pathname)) {
      startJourney('app_launch', undefined, { startTime: 0, cold: true, routes: [HOME_LAUNCH_PATH] });
    }
  } catch {
    // ignore
  }
}

// ── React helpers ─────────────────────────────────────────────────────────

/** Runs `task` after the next frame has painted. Returns a cancel function. */
function afterNextPaint(task: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
    const timer = setTimeout(task, 0);
    return () => clearTimeout(timer);
  }
  let timer: ReturnType<typeof setTimeout> | null = null;
  const frame = window.requestAnimationFrame(() => {
    // rAF runs before paint; the macrotask after it runs once the frame is out.
    timer = setTimeout(task, 0);
  });
  return () => {
    window.cancelAnimationFrame(frame);
    if (timer !== null) clearTimeout(timer);
  };
}

/**
 * Ends the active journey `name` once `ready` is true and the frame showing
 * the result has painted. A no-op when that journey is not running.
 */
export function useJourneyEnd(name: JourneyName, ready: boolean, extra?: JourneyMeta): void {
  const extraRef = React.useRef(extra);
  React.useEffect(() => {
    extraRef.current = extra;
  });
  React.useEffect(() => {
    if (!ready) return;
    try {
      return afterNextPaint(() => {
        endJourney(name, extraRef.current);
      });
    } catch {
      return undefined;
    }
  }, [name, ready]);
}

/**
 * Renders nothing; ends `name` after the paint that first shows it. For
 * components whose readiness is only known after their early returns.
 */
export function JourneyEnd({ name, extra }: { name: JourneyName; extra?: JourneyMeta }): null {
  useJourneyEnd(name, true, extra);
  return null;
}

/**
 * For surfaces that open in place (paywalls): starts `name` when `open`
 * turns true (from the tap that opened it, if recent), ends it once `ready`
 * has painted, and drops it if the surface closes first.
 */
export function useOpenJourney(
  name: JourneyName,
  open: boolean,
  ready: boolean,
  meta?: JourneyMeta,
  endExtra?: JourneyMeta,
): void {
  const handleRef = React.useRef<JourneyHandle | null>(null);
  const metaRef = React.useRef(meta);
  const endExtraRef = React.useRef(endExtra);
  // Declared first so the effects below read this render's values.
  React.useEffect(() => {
    metaRef.current = meta;
    endExtraRef.current = endExtra;
  });

  React.useEffect(() => {
    if (!open) return;
    const startTime = getRecentInteractionTime() ?? undefined;
    handleRef.current = startJourney(name, metaRef.current, { startTime });
    return () => {
      handleRef.current?.abandon('closed');
      handleRef.current = null;
    };
  }, [name, open]);

  React.useEffect(() => {
    if (!open || !ready) return;
    return afterNextPaint(() => {
      handleRef.current?.end(endExtraRef.current);
    });
  }, [open, ready]);
}

// ── Tests ─────────────────────────────────────────────────────────────────

/** Test-only: replaces the sender and clears all state. */
export function __resetJourneyTimingForTests(
  send?: (properties: Record<string, JourneyMetaValue>) => void,
): void {
  for (const run of Array.from(active.values())) run.abandon('manual');
  active.clear();
  startedNames.clear();
  lastInteractionAt = null;
  lastRoute = null;
  installed = false;
  sendImpl = send ?? defaultSend;
}

/** Test-only: records a tap at `time` (performance.now() timebase). */
export function __recordInteractionForTests(time: number): void {
  lastInteractionAt = time;
}
